import * as React from "react";
import { dragManager } from "./DragManager";
import { DragPayload, DropPosition } from "./types";

// Only changes when a drag starts or ends. Pointer position and the hovered
// drop zone are deliberately kept out of React state: they change on every
// mouse move and would re-render every widget on the page.
interface DragContextValue {
  isDragging: boolean;
  dragPayload: DragPayload | null;
}

const IDLE_STATE: DragContextValue = {
  isDragging: false,
  dragPayload: null,
};

const DragContext = React.createContext<DragContextValue>(IDLE_STATE);

interface DragProviderProps {
  children: React.ReactNode;
}

export const DragProvider: React.FC<DragProviderProps> = ({ children }) => {
  const [state, setState] = React.useState<DragContextValue>(IDLE_STATE);

  React.useEffect(() => {
    const unsubscribeStart = dragManager.onDragStart((payload) => {
      setState({
        isDragging: true,
        dragPayload: payload,
      });
    });

    const unsubscribeEnd = dragManager.onDragEnd(() => {
      setState(IDLE_STATE);
    });

    return () => {
      unsubscribeStart();
      unsubscribeEnd();
    };
  }, []);

  return <DragContext.Provider value={state}>{children}</DragContext.Provider>;
};

export const useDragState = () => React.useContext(DragContext);

export function useDragEnd(
  callback: (payload: DragPayload, position: DropPosition | null) => void,
  enabled: boolean = true,
) {
  React.useEffect(() => {
    if (!enabled) {
      return;
    }
    return dragManager.onDragEnd((payload, dropZone) => {
      callback(payload, dropZone?.payload || null);
    });
  }, [callback, enabled]);
}

type DragSelector<T> = (state: DragContextValue) => T;

export function useDragSelector<T>(selector: DragSelector<T>): T {
  const state = React.useContext(DragContext);
  return React.useMemo(() => selector(state), [selector, state]);
}

/**
 * True when a drag started in `scope` and was dropped on a drop zone of the
 * same scope. Several widget containers (e.g. stations) share one page and
 * receive every drop, so each must only handle its own.
 */
export function isDropInScope(
  payload: DragPayload,
  position: DropPosition | null,
  scope: string,
): position is DropPosition {
  return !!position && payload.scope === scope && position.scope === scope;
}

/**
 * Like useDragEnd, but only calls `handler` for drops that start and end in
 * `scope`. The latest `handler` is always used, so it need not be memoized.
 */
export function useScopedDragEnd(
  scope: string,
  handler: (payload: DragPayload, position: DropPosition) => void,
  enabled: boolean = true,
) {
  const handlerRef = React.useRef(handler);
  React.useEffect(() => {
    handlerRef.current = handler;
  });

  useDragEnd(
    React.useCallback(
      (payload: DragPayload, position: DropPosition | null) => {
        if (isDropInScope(payload, position, scope)) {
          handlerRef.current(payload, position);
        }
      },
      [scope],
    ),
    enabled,
  );
}
